import { useQuery } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { useDriverProfile } from './useDriverProfile';
import { QueryConfig } from '../api/client';
import { driverApi, DriverCall } from '../services/driverApi';

const getDriverCall = async (
  token: string,
  callId: string,
  companyId?: string
): Promise<DriverCall> => {
  driverApi.setAuthToken(token);
  return driverApi.getCall(callId, companyId);
};

/**
 * One call, from GET /calls/:id for every role. The backend decides whether
 * the caller may see it, so there is no choosing an endpoint by permission
 * here; a 404 means they may not.
 */
export const useDriverCall = (
  callId: string | undefined,
  config?: QueryConfig<typeof getDriverCall>
) => {
  const { getAccessTokenSilently, isAuthenticated } = useHybridAuth();
  const { data: profile, isLoading: isProfileLoading } = useDriverProfile();

  return useQuery({
    ...config,
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      if (!callId) {
        throw new Error('Missing call ID');
      }
      return getDriverCall(token, callId, profile?.companyId);
    },
    queryKey: ['driver-call', callId, profile?.companyId],
    enabled: isAuthenticated && !!callId && !!profile && !isProfileLoading,
    staleTime: 60 * 1000, // 60 seconds
    refetchOnMount: true, // Refetch when component mounts to get fresh data
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchOnReconnect: true, // Refetch on reconnection
    // No refetchInterval - manual fetching only
  });
};
